import type { RaRecord } from 'react-admin';

import { readProjectSettings } from './adminSettings';
import { readAdminSession } from './authProvider';

export const EXAM_TASK_REVIEW_RESOURCE = 'examTaskReviews' as const;
export const EXAM_TASK_DESTINATION_RESOURCE = 'examTaskDestinations' as const;

export type ExamTaskResource = typeof EXAM_TASK_REVIEW_RESOURCE | typeof EXAM_TASK_DESTINATION_RESOURCE;

export type ExamTaskRecord = RaRecord & {
    id: string;
    isActive?: boolean;
    isPublished?: boolean;
    isCopiedFromAcademic?: boolean;
    status?: string;
    [key: string]: unknown;
};

export type LiveExamTaskVoiceFileRecord = {
    examTaskMediaVoiceId: string;
    voiceLabel: string;
    upstreamFileKey: string | null;
    publishedFileKey: string | null;
    productionFileKey: string | null;
};

export type LiveExamTaskFilesRecord = {
    id: string;
    examTaskId: string;
    examName: string;
    hasProductionConnection: boolean;
    productionTaskExists: boolean;
    upstreamBucketName: string | null;
    publishedImageBucketName: string | null;
    publishedVoiceBucketName: string | null;
    upstreamImageKey: string | null;
    publishedImageKey: string | null;
    productionImageKey: string | null;
    voices: LiveExamTaskVoiceFileRecord[];
};

export type LiveExamTaskBulkFileSyncStatusRecord = {
    id: string;
    bulkFilePublishTaskId: string;
    status: string;
    requestedBy: string | null;
    requestTraceId: string | null;
    totalTaskCount: number;
    processedTaskCount: number;
    succeededTaskCount: number;
    failedTaskCount: number;
    lastExamTaskId: string | null;
    lastQuestionNo: string | null;
    lastError: string | null;
    startedAt: string | null;
    finishedAt: string | null;
    createdAt: string;
    updatedAt: string;
    isTerminal: boolean;
};

export type LiveExamTaskRecreateImageResponse = {
    sourceImageUrl: string;
    replicationPrompt: string;
    mediaResourceUrl: string;
    storageKey: string;
    size: string;
};

export type DescribeImageBlueprintValue = {
    label: string;
    value: number;
    unit: string | null;
};

export type DescribeImageBlueprint = {
    imageType: string;
    barArrangement: string;
    overview: string;
    keyElements: string[];
    relationships: string[];
    trends: string[];
    comparisons: string[];
    values: DescribeImageBlueprintValue[];
    safeImplications: string[];
    unsupportedClaimWarnings: string[];
    confidence: number;
};

export type DescribeImageBlueprintArtifact = {
    artifactId: string;
    examName: string;
    examTaskType: string;
    examTaskId: string;
    sourceSha256: string;
    payload: DescribeImageBlueprint;
    generatorProvider: string;
    generatorModel: string;
    promptVersion: string;
    schemaVersion: string;
    status: string;
    createdAtUtc: string;
    approvedAtUtc: string | null;
};

export type DescribeImageBlueprintDetail = {
    artifact: DescribeImageBlueprintArtifact | null;
    mediaResourceUrl: string | null;
    promptBody: string | null;
};

export type AiSourceArtifact = {
    artifactId: string;
    examName: string;
    examTaskType: string;
    examTaskId: string;
    artifactKind: string;
    sourceSha256: string;
    payload: Record<string, unknown>;
    generatorProvider: string;
    generatorModel: string;
    promptVersion: string;
    schemaVersion: string;
    status: string;
    qualityFlags: unknown;
    createdAtUtc: string;
    approvedAtUtc: string | null;
    retiredAtUtc: string | null;
};

export type AiSourceArtifactTaskDetail = {
    supported: boolean;
    artifact: AiSourceArtifact | null;
    mediaResourceUrl: string | null;
    promptBody: string | null;
    sourceMatchesCurrent: boolean | null;
};

export type SummarizeGroupDiscussionBlueprint = {
    topic: string;
    principalParticipants: Array<{
        participantKey: string;
        sourceLabels: string[];
        mainViewpoint: string;
        reasons: string[];
        importantDetails: string[];
    }>;
    relationships: Array<{
        participants: string[];
        relationshipType: string;
        description: string;
    }>;
    overallConclusion: string;
    essentialIdeas: string[];
    unsupportedClaimWarnings: string[];
    sourceQualityFlags: string[];
    confidence: number;
};

export type SummarizeGroupDiscussionBlueprintArtifact = Omit<DescribeImageBlueprintArtifact, 'payload'> & {
    payload: SummarizeGroupDiscussionBlueprint;
};

export type SummarizeGroupDiscussionBlueprintDetail = {
    artifact: SummarizeGroupDiscussionBlueprintArtifact | null;
    mediaResourceUrl: string | null;
    promptBody: string | null;
    sourceMatchesCurrent: boolean | null;
};

export type LiveExamTaskVoiceRegenerationStatusRecord = {
    id: string;
    questionVoiceGenerationRequestId: string;
    examTaskId: string;
    requestStatus: string;
    forceRegenerate: boolean;
    skipExistingCheck: boolean;
    sourceLabel: string | null;
    requestedBy: string | null;
    requestNote: string | null;
    attemptCount: number;
    lastError: string | null;
    requestedAt: string;
    startedAt: string | null;
    finishedAt: string | null;
    createdAt: string;
    updatedAt: string;
    isTerminal: boolean;
};

export type LiveExamTaskDuplicateCleanupItem = {
    examTaskId: string;
    publishedRefId: string;
    questionNo: string;
    displayTitle: string;
    wasMarkedPublished: boolean;
    matchedOnPrompt: boolean;
    matchedOnSegment: boolean;
    promptSimilarityPercentage: number;
    segmentSimilarityPercentage: number;
};

export type LiveExamTaskDuplicateCleanupResponse = {
    processedCount: number;
    matchedCount: number;
    markedPublishedCount: number;
    unmatchedCount: number;
    items: LiveExamTaskDuplicateCleanupItem[];
};

export type LiveExamTaskPublishStateSyncResponse = {
    hasProductionConnection: boolean;
    defaultTaskCount: number;
    productionTaskCount: number;
    matchedQuestionNoCount: number;
    alreadyPublishedCount: number;
    updatedCount: number;
    unmatchedCount: number;
};

export type AdminAiRewriteRequestDTO = {
    paragraph?: string | null;
    IsConservative?: boolean;
};

export type AdminAiRewriteResponseDTO = {
    suggestedTitle: string;
    rewrittenParagraph: string;
};

type RequestAdminControllerOptions = {
    controller?: string;
};

type AdminApiEnvelope<T> = {
    success?: boolean;
    data?: T;
    statusCode?: number;
    errorMessage?: string | null;
    message?: string;
    error?: string;
    title?: string;
    detail?: string;
}

function trimTrailingSlash(value: string): string {
    return value.replace(/\/+$/, '');
}

function trimSlashes(value: string): string {
    return value.replace(/^\/+|\/+$/g, '');
}

function hasVersionedApiPath(value: string): boolean {
    return /\/api\/v[^/]+$/i.test(trimTrailingSlash(value));
}

function normalizeNullableString(value: unknown): string | null {
    if (typeof value !== 'string') {
        return null;
    }

    const trimmed = value.trim();
    return trimmed ? trimmed : null;
}

function getAdminApiConfig() {
    const settings = readProjectSettings();
    const session = readAdminSession();
    const baseUrl = trimTrailingSlash(
        String(
            import.meta.env.VITE_API_BASE_URL ??
                import.meta.env.VITE_API_URL ??
                settings.adminApiBaseUrl ??
                'https://test-api.pteultra.com/api/v1',
        ),
    );
    const apiVersion = String(import.meta.env.VITE_API_VERSION ?? settings.adminApiVersion ?? '1');
    const adminToken = String(
        session?.adminToken ?? session?.accessToken ?? import.meta.env.VITE_ADMIN_TOKEN ?? settings.adminApiToken ?? '',
    );

    return { baseUrl, apiVersion, adminToken };
}

function getExamTaskController(resource: ExamTaskResource): string {
    if (resource === EXAM_TASK_REVIEW_RESOURCE) {
        return 'AdminExamTaskReview';
    }

    const settings = readProjectSettings();
    const controller = trimSlashes(
        String(import.meta.env.VITE_DESTINATION_EXAM_TASK_API_CONTROLLER ?? settings.destinationExamTaskApiController),
    );

    if (!controller) {
        throw new Error('Live exam task API controller is not configured in Project Settings');
    }

    return controller;
}

function getAdminAssetBaseUrl(): string {
    const fileBaseUrl = trimTrailingSlash(String(import.meta.env.VITE_BASE_FILE_URL ?? ''));
    if (fileBaseUrl) {
        return fileBaseUrl;
    }

    const { baseUrl } = getAdminApiConfig();
    if (hasVersionedApiPath(baseUrl)) {
        return trimTrailingSlash(baseUrl.replace(/\/api\/v[^/]+$/i, ''));
    }

    return trimTrailingSlash(baseUrl);
}

function resolveAdminAssetUrl(value: unknown, preferredBaseUrl?: string): string {
    const rawValue = String(value ?? '').trim();
    if (!rawValue) {
        return '';
    }

    if (/^https?:\/\//i.test(rawValue)) {
        return rawValue;
    }

    const baseUrl = trimTrailingSlash(preferredBaseUrl?.trim() || getAdminAssetBaseUrl());
    if (!baseUrl) {
        return rawValue;
    }

    return new URL(rawValue.replace(/^\/+/, ''), `${baseUrl}/`).toString();
}

function getErrorMessage(payload: unknown, fallback: string): string {
    if (typeof payload === 'string' && payload.trim()) return payload;
    if (payload && typeof payload === 'object') {
        const candidate = payload as Record<string, unknown>;
        const direct =
            candidate.errorMessage ?? candidate.message ?? candidate.error ?? candidate.title ?? candidate.detail;
        if (typeof direct === 'string' && direct.trim()) return direct;
    }
    return fallback;
}

function getBooleanValue(value: unknown): boolean | undefined {
    if (typeof value === 'boolean') {
        return value;
    }

    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (normalized === 'true') return true;
        if (normalized === 'false') return false;
    }

    if (typeof value === 'number') {
        if (value === 1) return true;
        if (value === 0) return false;
    }

    return undefined;
}

function getFirstBoolean(record: Record<string, unknown>, keys: string[]): boolean | undefined {
    for (const key of keys) {
        const value = getBooleanValue(record[key]);
        if (value !== undefined) {
            return value;
        }
    }

    return undefined;
}

function getFirstString(record: Record<string, unknown>, keys: string[]): string | undefined {
    for (const key of keys) {
        const value = record[key];
        if (typeof value === 'string' && value.trim()) {
            return value;
        }
    }

    return undefined;
}

function inferExamTaskActive(record: Record<string, unknown>): boolean {
    const explicitFlag = getFirstBoolean(record, [
        'isActive',
        'active',
        'isActivated',
        'activated',
        'isPublished',
        'published',
    ]);
    if (explicitFlag !== undefined) {
        return explicitFlag;
    }

    const status = getFirstString(record, ['status', 'state', 'publishedState', 'activationStatus', 'liveStatus']);
    if (status) {
        const normalized = status.trim().toLowerCase();
        if (['active', 'activated', 'published', 'live', 'enabled'].includes(normalized)) {
            return true;
        }

        if (['inactive', 'deactivated', 'unpublished', 'draft', 'disabled'].includes(normalized)) {
            return false;
        }
    }

    return Boolean(getFirstString(record, ['activatedAt', 'publishedAt', 'publishedAtUtc', 'liveAt']));
}

export function isExamTaskRecordActive(record: ExamTaskRecord | null | undefined): boolean {
    if (!record) {
        return false;
    }

    return inferExamTaskActive(record as Record<string, unknown>);
}

export function isExamTaskRecordPublished(record: ExamTaskRecord | null | undefined): boolean {
    if (!record) {
        return false;
    }

    return getBooleanValue(record.isPublished) ?? false;
}

function unwrapAdminApiPayload<T>(payload: T | AdminApiEnvelope<T>): T {
    if (!payload || typeof payload !== 'object' || !('data' in payload)) {
        return payload as T;
    }

    const envelope = payload as AdminApiEnvelope<T>;

    if (envelope.success === false) {
        throw new Error(getErrorMessage(envelope, 'Admin exam task request failed'));
    }

    if (envelope.data !== undefined) {
        return envelope.data;
    }

    return payload as T;
}
function normalizeExamTaskRecord(item: unknown, fallbackId?: string): ExamTaskRecord {
    const record = (item ?? {}) as Record<string, unknown>;
    const rawId = record.id ?? record.examTaskId ?? fallbackId;

    if (rawId === undefined || rawId === null || rawId === '') {
        throw new Error('Exam task item is missing an id');
    }

    const isActive = inferExamTaskActive(record);
    const isPublished = getBooleanValue(record.isPublished) ?? false;

    return {
        ...record,
        id: String(rawId),
        isActive,
        isPublished,
        status:
            typeof record.status === 'string' && record.status.trim() ? record.status : isActive ? 'Active' : 'Inactive',
    };
}

function normalizeLiveExamTaskVoiceFileRecord(item: unknown): LiveExamTaskVoiceFileRecord {
    const record = (item ?? {}) as Record<string, unknown>;

    return {
        examTaskMediaVoiceId: String(record.examTaskMediaVoiceId ?? record.id ?? ''),
        voiceLabel: String(record.voiceLabel ?? 'Voice'),
        upstreamFileKey: normalizeNullableString(record.upstreamFileKey),
        publishedFileKey: normalizeNullableString(record.publishedFileKey),
        productionFileKey: normalizeNullableString(record.productionFileKey),
    };
}

function normalizeLiveExamTaskFilesRecord(item: unknown, fallbackId?: string): LiveExamTaskFilesRecord {
    const record = (item ?? {}) as Record<string, unknown>;
    const rawId = record.id ?? record.examTaskId ?? fallbackId;

    if (rawId === undefined || rawId === null || rawId === '') {
        throw new Error('Live exam task files response is missing an id');
    }

    return {
        id: String(rawId),
        examTaskId: String(record.examTaskId ?? rawId),
        examName: String(record.examName ?? ''),
        hasProductionConnection: Boolean(record.hasProductionConnection),
        productionTaskExists: Boolean(record.productionTaskExists),
        upstreamBucketName: normalizeNullableString(record.upstreamBucketName),
        publishedImageBucketName: normalizeNullableString(record.publishedImageBucketName),
        publishedVoiceBucketName: normalizeNullableString(record.publishedVoiceBucketName),
        upstreamImageKey: normalizeNullableString(record.upstreamImageKey),
        publishedImageKey: normalizeNullableString(record.publishedImageKey),
        productionImageKey: normalizeNullableString(record.productionImageKey),
        voices: Array.isArray(record.voices) ? record.voices.map(normalizeLiveExamTaskVoiceFileRecord) : [],
    };
}

function normalizeLiveExamTaskBulkFileSyncStatusRecord(
    item: unknown,
    fallbackId?: string,
): LiveExamTaskBulkFileSyncStatusRecord {
    const record = (item ?? {}) as Record<string, unknown>;
    const rawId = record.id ?? record.bulkFilePublishTaskId ?? fallbackId;

    if (rawId === undefined || rawId === null || rawId === '') {
        throw new Error('Live exam task bulk file sync status response is missing an id');
    }

    const status = String(record.status ?? '');
    const normalizedStatus = status.trim().toLowerCase();

    return {
        id: String(rawId),
        bulkFilePublishTaskId: String(record.bulkFilePublishTaskId ?? rawId),
        status,
        requestedBy: normalizeNullableString(record.requestedBy),
        requestTraceId: normalizeNullableString(record.requestTraceId),
        totalTaskCount: Number(record.totalTaskCount ?? 0),
        processedTaskCount: Number(record.processedTaskCount ?? 0),
        succeededTaskCount: Number(record.succeededTaskCount ?? 0),
        failedTaskCount: Number(record.failedTaskCount ?? 0),
        lastExamTaskId: normalizeNullableString(record.lastExamTaskId),
        lastQuestionNo: normalizeNullableString(record.lastQuestionNo),
        lastError: normalizeNullableString(record.lastError),
        startedAt: normalizeNullableString(record.startedAt),
        finishedAt: normalizeNullableString(record.finishedAt),
        createdAt: String(record.createdAt ?? ''),
        updatedAt: String(record.updatedAt ?? ''),
        isTerminal: ['succeeded', 'completed_with_errors', 'failed', 'stopped'].includes(normalizedStatus),
    };
}

function normalizeLiveExamTaskRecreateImageResponse(item: unknown): LiveExamTaskRecreateImageResponse {
    const record = (item ?? {}) as Record<string, unknown>;

    return {
        sourceImageUrl: String(record.sourceImageUrl ?? ''),
        replicationPrompt: String(record.replicationPrompt ?? ''),
        mediaResourceUrl: String(record.mediaResourceUrl ?? ''),
        storageKey: String(record.storageKey ?? ''),
        size: String(record.size ?? ''),
    };
}

function normalizeLiveExamTaskVoiceRegenerationStatusRecord(item: unknown, fallbackId?: string): LiveExamTaskVoiceRegenerationStatusRecord {
    const record = (item ?? {}) as Record<string, unknown>;
    const rawId = record.id ?? record.questionVoiceGenerationRequestId ?? fallbackId;

    if (rawId === undefined || rawId === null || rawId === '') {
        throw new Error('Live exam task voice regeneration status response is missing an id');
    }

    return {
        id: String(rawId),
        questionVoiceGenerationRequestId: String(record.questionVoiceGenerationRequestId ?? rawId),
        examTaskId: String(record.examTaskId ?? ''),
        requestStatus: String(record.requestStatus ?? ''),
        forceRegenerate: Boolean(record.forceRegenerate),
        skipExistingCheck: Boolean(record.skipExistingCheck),
        sourceLabel: normalizeNullableString(record.sourceLabel),
        requestedBy: normalizeNullableString(record.requestedBy),
        requestNote: normalizeNullableString(record.requestNote),
        attemptCount: Number(record.attemptCount ?? 0),
        lastError: normalizeNullableString(record.lastError),
        requestedAt: String(record.requestedAt ?? ''),
        startedAt: normalizeNullableString(record.startedAt),
        finishedAt: normalizeNullableString(record.finishedAt),
        createdAt: String(record.createdAt ?? ''),
        updatedAt: String(record.updatedAt ?? ''),
        isTerminal: Boolean(record.isTerminal),
    };
}

function normalizeLiveExamTaskDuplicateCleanupItem(item: unknown): LiveExamTaskDuplicateCleanupItem {
    const record = (item ?? {}) as Record<string, unknown>;

    return {
        examTaskId: String(record.examTaskId ?? ''),
        publishedRefId: String(record.publishedRefId ?? ''),
        questionNo: String(record.questionNo ?? ''),
        displayTitle: String(record.displayTitle ?? ''),
        wasMarkedPublished: Boolean(record.wasMarkedPublished),
        matchedOnPrompt: Boolean(record.matchedOnPrompt),
        matchedOnSegment: Boolean(record.matchedOnSegment),
        promptSimilarityPercentage: Number(record.promptSimilarityPercentage ?? 0),
        segmentSimilarityPercentage: Number(record.segmentSimilarityPercentage ?? 0),
        };
}

function normalizeLiveExamTaskDuplicateCleanupResponse(item: unknown): LiveExamTaskDuplicateCleanupResponse {
    const record = (item ?? {}) as Record<string, unknown>;

    return {
        processedCount: Number(record.processedCount ?? 0),
        matchedCount: Number(record.matchedCount ?? 0),
        markedPublishedCount: Number(record.markedPublishedCount ?? 0),
        unmatchedCount: Number(record.unmatchedCount ?? 0),
        items: Array.isArray(record.items) ? record.items.map(normalizeLiveExamTaskDuplicateCleanupItem) : [],
    };
}

function normalizeLiveExamTaskPublishStateSyncResponse(item: unknown): LiveExamTaskPublishStateSyncResponse {
    const record = (item ?? {}) as Record<string, unknown>;

    return {
        hasProductionConnection: Boolean(record.hasProductionConnection),
        defaultTaskCount: Number(record.defaultTaskCount ?? 0),
        productionTaskCount: Number(record.productionTaskCount ?? 0),
        matchedQuestionNoCount: Number(record.matchedQuestionNoCount ?? 0),
        alreadyPublishedCount: Number(record.alreadyPublishedCount ?? 0),
        updatedCount: Number(record.updatedCount ?? 0),
        unmatchedCount: Number(record.unmatchedCount ?? 0),
    };
}

function normalizeAdminAiRewriteResponse(item: unknown): AdminAiRewriteResponseDTO {
    const record = (item ?? {}) as Record<string, unknown>;

    return {
        suggestedTitle: String(record.suggestedTitle ?? ''),
        rewrittenParagraph: String(record.rewrittenParagraph ?? ''),
    };
}

function normalizeDescribeImageBlueprintDetail(item: unknown): DescribeImageBlueprintDetail {
    const detail = (item ?? {}) as Record<string, unknown>;
    const artifactValue = detail.artifact;
    const artifact = artifactValue && typeof artifactValue === 'object'
        ? artifactValue as DescribeImageBlueprintArtifact
        : null;

    return {
        artifact,
        mediaResourceUrl: normalizeNullableString(detail.mediaResourceUrl),
        promptBody: normalizeNullableString(detail.promptBody),
    };
}

async function requestAdminExamTask<T>(
    resource: ExamTaskResource,
    path: string,
    init: RequestInit = {},
    query?: Record<string, string>,
    options?: RequestAdminControllerOptions,
): Promise<T> {
    const { baseUrl, apiVersion, adminToken } = getAdminApiConfig();
    const apiRoot = hasVersionedApiPath(baseUrl) ? baseUrl : `${baseUrl}/api/v${apiVersion}`;
    const controller = trimSlashes(options?.controller ?? getExamTaskController(resource));
    const url = new URL(`${apiRoot}/${controller}/${trimSlashes(path)}`);

    for (const [key, value] of Object.entries(query ?? {})) {
        if (value !== '') {
            url.searchParams.set(key, value);
        }
    }

    const headers = new Headers(init.headers);
    if (!headers.has('Accept')) {
        headers.set('Accept', 'application/json');
    }
    if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
    }
    if (adminToken && !headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${adminToken}`);
    }

    const response = await fetch(url.toString(), {
        ...init,
        headers,
    });

    const contentType = response.headers.get('content-type') ?? '';
    const isJson = contentType.includes('application/json');
    const payload = isJson ? await response.json() : await response.text();

    if (!response.ok) {
        throw new Error(getErrorMessage(payload, `${response.status} ${response.statusText}`));
    }

    if (payload && typeof payload === 'object' && 'success' in (payload as Record<string, unknown>)) {
        const envelope = payload as AdminApiEnvelope<unknown>;
        if (envelope.success === false) {
            throw new Error(getErrorMessage(envelope, 'Admin exam task request failed'));
        }
    }

    return payload as T;
}

export async function getExamTaskList(resource: ExamTaskResource, params: any) {
    const { page = 1, perPage = 10 } = params.pagination ?? {};
    const { field = 'updatedAt', order = 'DESC' } = params.sort ?? {};
    const filter = (params.filter ?? {}) as Record<string, unknown>;
    const examTaskType = filter.examTaskType;
    const examName = filter.examName;
    const search = filter.q;

    const apiFilter = Object.fromEntries(
        Object.entries(filter).filter(
            ([key, value]) =>
                key !== 'examTaskType' &&
                key !== 'examName' &&
                key !== 'q' &&
                value !== undefined &&
                value !== null &&
                value !== '',
        ),
    );

    const response = unwrapAdminApiPayload(
        await requestAdminExamTask<
            | {
                  total?: number;
                  items?: unknown[];
              }
            | AdminApiEnvelope<{
                  total?: number;
                  items?: unknown[];
              }>
        >(resource, 'get-list', { method: 'GET' }, {
            page: String(page),
            perPage: String(perPage),
            sort: String(field),
            order: String(order),
            ...(typeof search === 'string' && search.trim() ? { q: search.trim() } : {}),
            ...(typeof examTaskType === 'string' && examTaskType ? { examTaskType } : {}),
            ...(typeof examName === 'string' && examName ? { examName } : {}),
            ...(Object.keys(apiFilter).length > 0 ? { filter: JSON.stringify(apiFilter) } : {}),
        }),
    );

    const items = Array.isArray(response.items) ? response.items : [];
    return {
        data: items.map((item) => normalizeExamTaskRecord(item)),
        total: typeof response.total === 'number' ? response.total : items.length,
    };
}

export async function getExamTask(resource: ExamTaskResource, params: any) {
    const item = unwrapAdminApiPayload(
        await requestAdminExamTask<unknown | AdminApiEnvelope<unknown>>(resource, `get-one/${params.id}`, {
            method: 'GET',
        }),
    );

    return { data: normalizeExamTaskRecord(item, String(params.id)) };
}

export async function getManyExamTasks(resource: ExamTaskResource, params: any) {
    const data = await Promise.all(
        params.ids.map(async (id: string) => {
            const item = unwrapAdminApiPayload(
                await requestAdminExamTask<unknown | AdminApiEnvelope<unknown>>(resource, `get-one/${id}`, {
                    method: 'GET',
                }),
            );

            return normalizeExamTaskRecord(item, String(id));
        }),
    );

    return { data };
}

export async function getLiveExamTaskFiles(examTaskId: string): Promise<LiveExamTaskFilesRecord> {
    const item = unwrapAdminApiPayload(
        await requestAdminExamTask<unknown | AdminApiEnvelope<unknown>>(
            EXAM_TASK_DESTINATION_RESOURCE,
            `get-files/${examTaskId}`,
            {
                method: 'GET',
            },
        ),
    );

    return normalizeLiveExamTaskFilesRecord(item, examTaskId);
}

export async function syncLiveExamTaskFilesToProduction(examTaskId: string): Promise<LiveExamTaskFilesRecord> {
    const item = unwrapAdminApiPayload(
        await requestAdminExamTask<unknown | AdminApiEnvelope<unknown>>(
            EXAM_TASK_DESTINATION_RESOURCE,
            `sync-files/${examTaskId}`,
            {
                method: 'POST',
            },
        ),
    );

    return normalizeLiveExamTaskFilesRecord(item, examTaskId);
}

export async function queueLiveExamTaskBulkFileSync(): Promise<LiveExamTaskBulkFileSyncStatusRecord> {
    const item = unwrapAdminApiPayload(
        await requestAdminExamTask<unknown | AdminApiEnvelope<unknown>>(
            EXAM_TASK_DESTINATION_RESOURCE,
            'sync-files-all',
            {
                method: 'POST',
            },
        ),
    );

    return normalizeLiveExamTaskBulkFileSyncStatusRecord(item);
}

export async function stopLiveExamTaskBulkFileSync(
    bulkFilePublishTaskId: string,
): Promise<LiveExamTaskBulkFileSyncStatusRecord> {
    const item = unwrapAdminApiPayload(
        await requestAdminExamTask<unknown | AdminApiEnvelope<unknown>>(
            EXAM_TASK_DESTINATION_RESOURCE,
            `sync-files-all-stop/${bulkFilePublishTaskId}`,
            {
                method: 'POST',
            },
        ),
    );

    return normalizeLiveExamTaskBulkFileSyncStatusRecord(item, bulkFilePublishTaskId);
}

export async function getLiveExamTaskBulkFileSyncStatus(
    bulkFilePublishTaskId: string,
): Promise<LiveExamTaskBulkFileSyncStatusRecord> {
    const item = unwrapAdminApiPayload(
        await requestAdminExamTask<unknown | AdminApiEnvelope<unknown>>(
            EXAM_TASK_DESTINATION_RESOURCE,
            `sync-files-all-status/${bulkFilePublishTaskId}`,
            {
                method: 'GET',
            },
        ),
    );

    return normalizeLiveExamTaskBulkFileSyncStatusRecord(item, bulkFilePublishTaskId);
}

export async function deleteLiveExamTaskVoice(examTaskId: string, voiceId: string): Promise<void> {
    await requestAdminExamTask<unknown | AdminApiEnvelope<unknown>>(
        EXAM_TASK_DESTINATION_RESOURCE,
        `delete-voice/${examTaskId}/${voiceId}`,
        {
            method: 'DELETE',
        },
    );
}

export async function deleteLiveExamTaskVoices(examTaskId: string): Promise<void> {
    await requestAdminExamTask<unknown | AdminApiEnvelope<unknown>>(
        EXAM_TASK_DESTINATION_RESOURCE,
        `delete-voices/${examTaskId}`,
        {
            method: 'DELETE',
        },
    );
}

export async function regenerateLiveExamTaskVoice(examTaskId: string): Promise<void> {
    await requestAdminExamTask<unknown | AdminApiEnvelope<unknown>>(
        EXAM_TASK_DESTINATION_RESOURCE,
        `regenerate-voice/${examTaskId}`,
        {
            method: 'POST',
        },
    );
}

export async function getLiveExamTaskVoiceRegenerationStatus(examTaskId: string): Promise<LiveExamTaskVoiceRegenerationStatusRecord> {
    const item = unwrapAdminApiPayload(
        await requestAdminExamTask<unknown | AdminApiEnvelope<unknown>>(
            EXAM_TASK_DESTINATION_RESOURCE,
            `regenerate-voice-status/${examTaskId}`,
            {
                method: 'GET',
            },
        ),
    );

    return normalizeLiveExamTaskVoiceRegenerationStatusRecord(item, examTaskId);
}

export async function syncReviewExamTasksWithPublished(): Promise<LiveExamTaskDuplicateCleanupResponse> {
    const item = unwrapAdminApiPayload(
        await requestAdminExamTask<unknown | AdminApiEnvelope<unknown>>(
            EXAM_TASK_DESTINATION_RESOURCE,
            'remove-duplicates',
            {
                method: 'POST',
            },
        ),
    );

    return normalizeLiveExamTaskDuplicateCleanupResponse(item);
}

export async function syncLiveExamTaskPublishState(): Promise<LiveExamTaskPublishStateSyncResponse> {
    const item = unwrapAdminApiPayload(
        await requestAdminExamTask<unknown | AdminApiEnvelope<unknown>>(
            EXAM_TASK_DESTINATION_RESOURCE,
            'sync-publish-state',
            {
                method: 'POST',
            },
        ),
    );

    return normalizeLiveExamTaskPublishStateSyncResponse(item);
}

export async function recreateLiveExamTaskImageFromUrl(
    examTaskId: string,
    imageUrl: string,
): Promise<LiveExamTaskRecreateImageResponse> {
    const item = unwrapAdminApiPayload(
        await requestAdminExamTask<unknown | AdminApiEnvelope<unknown>>(
            EXAM_TASK_DESTINATION_RESOURCE,
            `recreate-image-from-url/${examTaskId}`,
            {
                method: 'POST',
                body: JSON.stringify({ imageUrl }),
            },
        ),
    );

    return normalizeLiveExamTaskRecreateImageResponse(item);
}

export async function getDescribeImageBlueprint(
    examTaskId: string,
    examName: string,
): Promise<DescribeImageBlueprintDetail> {
    const response = await requestAdminExamTask<AdminApiEnvelope<DescribeImageBlueprintDetail>>(
        EXAM_TASK_DESTINATION_RESOURCE,
        `artifacts/describe-image/task/${encodeURIComponent(examTaskId)}`,
        { method: 'GET' },
        { examName },
        { controller: 'AdminAiScoring' },
    );
    return normalizeDescribeImageBlueprintDetail(unwrapAdminApiPayload(response));
}

export async function getAiSourceArtifactForTask(
    examTaskId: string,
    examName: string,
    examTaskType: string,
): Promise<AiSourceArtifactTaskDetail> {
    const response = await requestAdminExamTask<AdminApiEnvelope<AiSourceArtifactTaskDetail>>(
        EXAM_TASK_DESTINATION_RESOURCE,
        `artifacts/task/${encodeURIComponent(examTaskId)}`,
        { method: 'GET' },
        { examName, examTaskType },
        { controller: 'AdminAiScoring' },
    );
    return unwrapAdminApiPayload(response);
}

export async function generateAiSourceArtifactForTask(
    examTaskId: string,
    examName: string,
    examTaskType: string,
    force: boolean,
): Promise<AiSourceArtifact> {
    const response = await requestAdminExamTask<AdminApiEnvelope<AiSourceArtifact>>(
        EXAM_TASK_DESTINATION_RESOURCE,
        `artifacts/task/${encodeURIComponent(examTaskId)}/generate`,
        {
            method: 'POST',
            body: JSON.stringify({ examName, examTaskType, force }),
        },
        undefined,
        { controller: 'AdminAiScoring' },
    );
    return unwrapAdminApiPayload(response);
}

export async function saveAiSourceArtifactPayload(
    artifactId: string,
    payload: Record<string, unknown>,
    notes?: string,
): Promise<AiSourceArtifact> {
    const response = await requestAdminExamTask<AdminApiEnvelope<AiSourceArtifact>>(
        EXAM_TASK_DESTINATION_RESOURCE,
        `artifacts/${encodeURIComponent(artifactId)}/payload`,
        {
            method: 'PUT',
            body: JSON.stringify({ payload, notes: notes?.trim() || null }),
        },
        undefined,
        { controller: 'AdminAiScoring' },
    );
    return unwrapAdminApiPayload(response);
}

export async function reviewGenericAiSourceArtifact(
    artifactId: string,
    decision: 'Approve' | 'Reject',
    notes?: string,
): Promise<AiSourceArtifact> {
    const response = await requestAdminExamTask<AdminApiEnvelope<AiSourceArtifact>>(
        EXAM_TASK_DESTINATION_RESOURCE,
        `artifacts/${encodeURIComponent(artifactId)}/review`,
        {
            method: 'POST',
            body: JSON.stringify({ decision, reasonCodes: ['AdminReview'], notes: notes?.trim() || null }),
        },
        undefined,
        { controller: 'AdminAiScoring' },
    );
    return unwrapAdminApiPayload(response);
}

export async function generateDescribeImageBlueprint(
    examTaskId: string,
    examName: string,
    force: boolean,
): Promise<DescribeImageBlueprintArtifact> {
    const response = await requestAdminExamTask<AdminApiEnvelope<DescribeImageBlueprintArtifact>>(
        EXAM_TASK_DESTINATION_RESOURCE,
        `artifacts/describe-image/${encodeURIComponent(examTaskId)}/generate`,
        {
            method: 'POST',
            body: JSON.stringify({ examName, force, approve: false }),
        },
        undefined,
        { controller: 'AdminAiScoring' },
    );
    return unwrapAdminApiPayload(response);
}

export async function saveDescribeImageBlueprint(
    artifactId: string,
    blueprint: DescribeImageBlueprint,
    notes?: string,
): Promise<DescribeImageBlueprintArtifact> {
    const response = await requestAdminExamTask<AdminApiEnvelope<DescribeImageBlueprintArtifact>>(
        EXAM_TASK_DESTINATION_RESOURCE,
        `artifacts/describe-image/${encodeURIComponent(artifactId)}`,
        {
            method: 'PUT',
            body: JSON.stringify({ blueprint, notes: notes?.trim() || null }),
        },
        undefined,
        { controller: 'AdminAiScoring' },
    );
    return unwrapAdminApiPayload(response);
}

export async function getSummarizeGroupDiscussionBlueprint(
    examTaskId: string,
    examName: string,
): Promise<SummarizeGroupDiscussionBlueprintDetail> {
    const response = await requestAdminExamTask<AdminApiEnvelope<SummarizeGroupDiscussionBlueprintDetail>>(
        EXAM_TASK_DESTINATION_RESOURCE,
        `artifacts/summarize-group-discussion/task/${encodeURIComponent(examTaskId)}`,
        { method: 'GET' },
        { examName },
        { controller: 'AdminAiScoring' },
    );
    return unwrapAdminApiPayload(response);
}

export async function generateSummarizeGroupDiscussionBlueprint(
    examTaskId: string,
    force: boolean,
): Promise<SummarizeGroupDiscussionBlueprintArtifact> {
    const response = await requestAdminExamTask<AdminApiEnvelope<SummarizeGroupDiscussionBlueprintArtifact>>(
        EXAM_TASK_DESTINATION_RESOURCE,
        `artifacts/summarize-group-discussion/${encodeURIComponent(examTaskId)}/generate`,
        {
            method: 'POST',
            body: JSON.stringify({ examName: 'Academic', force, approve: false }),
        },
        undefined,
        { controller: 'AdminAiScoring' },
    );
    return unwrapAdminApiPayload(response);
}

export async function saveSummarizeGroupDiscussionBlueprint(
    artifactId: string,
    blueprint: SummarizeGroupDiscussionBlueprint,
    notes?: string,
): Promise<SummarizeGroupDiscussionBlueprintArtifact> {
    const response = await requestAdminExamTask<AdminApiEnvelope<SummarizeGroupDiscussionBlueprintArtifact>>(
        EXAM_TASK_DESTINATION_RESOURCE,
        `artifacts/summarize-group-discussion/${encodeURIComponent(artifactId)}`,
        {
            method: 'PUT',
            body: JSON.stringify({ blueprint, notes: notes?.trim() || null }),
        },
        undefined,
        { controller: 'AdminAiScoring' },
    );
    return unwrapAdminApiPayload(response);
}

export async function reviewAiSourceArtifact(
    artifactId: string,
    decision: 'Approve' | 'Reject',
    notes?: string,
): Promise<SummarizeGroupDiscussionBlueprintArtifact> {
    const response = await requestAdminExamTask<AdminApiEnvelope<SummarizeGroupDiscussionBlueprintArtifact>>(
        EXAM_TASK_DESTINATION_RESOURCE,
        `artifacts/${encodeURIComponent(artifactId)}/review`,
        {
            method: 'POST',
            body: JSON.stringify({ decision, reasonCodes: ['AdminReview'], notes: notes?.trim() || null }),
        },
        undefined,
        { controller: 'AdminAiScoring' },
    );
    return unwrapAdminApiPayload(response);
}

export async function retireAiSourceArtifact(artifactId: string): Promise<void> {
    await requestAdminExamTask<AdminApiEnvelope<unknown>>(
        EXAM_TASK_DESTINATION_RESOURCE,
        `artifacts/${encodeURIComponent(artifactId)}/retire`,
        { method: 'POST' },
        undefined,
        { controller: 'AdminAiScoring' },
    );
}

export async function copyAcademicLiveExamTaskToCore(examTaskId: string): Promise<ExamTaskRecord> {
    const item = unwrapAdminApiPayload(
        await requestAdminExamTask<unknown | AdminApiEnvelope<unknown>>(
            EXAM_TASK_DESTINATION_RESOURCE,
            `copy-academic-to-core/${examTaskId}`,
            {
                method: 'POST',
            },
            undefined,
            { controller: 'AdminExamTaskCreate' },
        ),
    );

    return normalizeExamTaskRecord(item);
}

export async function rewriteAdminAiContent(
    request: AdminAiRewriteRequestDTO,
): Promise<AdminAiRewriteResponseDTO> {
    const { baseUrl, apiVersion, adminToken } = getAdminApiConfig();
    const apiRoot = hasVersionedApiPath(baseUrl) ? baseUrl : `${baseUrl}/api/v${apiVersion}`;
    const url = new URL(`${apiRoot}/AdminAiRewrite/rewrite`);
    const headers = new Headers({
        Accept: 'application/json',
        'Content-Type': 'application/json',
    });

    if (adminToken) {
        headers.set('Authorization', `Bearer ${adminToken}`);
    }

    const response = await fetch(url.toString(), {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
    });

    const contentType = response.headers.get('content-type') ?? '';
    const isJson = contentType.includes('application/json');
    const payload = isJson ? await response.json() : await response.text();

    if (!response.ok) {
        throw new Error(getErrorMessage(payload, `${response.status} ${response.statusText}`));
    }

    const item = unwrapAdminApiPayload(payload as unknown | AdminApiEnvelope<unknown>);
    return normalizeAdminAiRewriteResponse(item);
}

export function resolveExamTaskImageAssetUrl(value: unknown): string {
    const environment = String(import.meta.env.VITE_ENVMENT ?? '').trim().toLowerCase();
    const preferredBaseUrl =
        environment === 'dev'
            ? String(import.meta.env.VITE_BASE_FILE_URL ?? import.meta.env.VITE_EXAM_TASK_FILE_URL ?? '')
            : String(import.meta.env.VITE_EXAM_TASK_FILE_URL ?? import.meta.env.VITE_BASE_FILE_URL ?? '');

    return resolveAdminAssetUrl(
        value,
        preferredBaseUrl,
    );
}

export function resolveExamTaskVoiceAssetUrl(value: unknown): string {
    return resolveAdminAssetUrl(
        value,
        String(import.meta.env.VITE_EXAM_TASK_FILE_URL ?? import.meta.env.VITE_BASE_FILE_URL ?? ''),
    );
}

export function resolvePublishedExamTaskAssetUrl(value: unknown): string {
    return resolveAdminAssetUrl(
        value,
        String(
            import.meta.env.VITE_EXAM_TASK_FILE_PUBLISH_URL ??
                import.meta.env.VITE_EXAM_TASK_FILE_URL ??
                import.meta.env.VITE_BASE_FILE_URL ??
                '',
        ),
    );
}

export async function updateExamTask(resource: ExamTaskResource, params: any) {
    const fallbackAction = typeof params.meta?.action === 'string' ? params.meta.action : '';
    const explicitActionPath = typeof params.meta?.actionPath === 'string' ? params.meta.actionPath : '';
    const requestMethod = params.meta?.method === 'DELETE' ? 'DELETE' : 'POST';
    const actionPath = trimSlashes(explicitActionPath || fallbackAction || (resource === EXAM_TASK_DESTINATION_RESOURCE ? 'update' : ''));

    if (!actionPath) {
        throw new Error('Unsupported exam task action');
    }

    const body =
        resource === EXAM_TASK_DESTINATION_RESOURCE && actionPath === 'update' && requestMethod === 'POST'
            ? params.data instanceof FormData
                ? params.data
                : JSON.stringify(params.data ?? {})
            : undefined;

    const response = await requestAdminExamTask<unknown | AdminApiEnvelope<unknown>>(resource, `${actionPath}/${params.id}`, {
        method: requestMethod,
        ...(body ? { body } : {}),
    });

    if (resource === EXAM_TASK_DESTINATION_RESOURCE && actionPath === 'update' && requestMethod === 'POST') {
        const refreshed = await requestAdminExamTask<unknown | AdminApiEnvelope<unknown>>(resource, `get-one/${params.id}`, {
            method: 'GET',
        });

        return { data: normalizeExamTaskRecord(unwrapAdminApiPayload(refreshed), String(params.id)) };
    }

    const payload = unwrapAdminApiPayload(response);
    if (payload && typeof payload === 'object') {
        return { data: normalizeExamTaskRecord(payload, String(params.id)) };
    }

    return {
        data: normalizeExamTaskRecord(
            resource === EXAM_TASK_DESTINATION_RESOURCE
                ? { ...(params.previousData ?? {}), ...(params.data ?? {}), id: params.id }
                : (params.previousData ?? { id: params.id }),
            String(params.id),
        ),
    };
}

export async function createExamTask(resource: ExamTaskResource, params: any) {
    if (resource !== EXAM_TASK_REVIEW_RESOURCE) {
        throw new Error('Creating live exam tasks is not supported.');
    }

    const body =
        params.data instanceof FormData
            ? params.data
            : JSON.stringify(params.data ?? {});

    const created = unwrapAdminApiPayload(
        await requestAdminExamTask<unknown | AdminApiEnvelope<unknown>>(
            resource,
            'create',
            {
                method: 'POST',
                body,
            },
            undefined,
            { controller: 'AdminExamTaskCreate' },
        ),
    );

    return { data: normalizeExamTaskRecord(created) };
}

export async function deleteExamTask(resource: ExamTaskResource, params: any) {
    if (resource === EXAM_TASK_REVIEW_RESOURCE) {
        return updateExamTask(resource, { ...params, meta: { ...(params.meta ?? {}), action: 'reject' } });
    }

    await requestAdminExamTask<boolean | AdminApiEnvelope<boolean>>(resource, `delete/${params.id}`, {
        method: 'DELETE',
    });

    return {
        data: normalizeExamTaskRecord(params.previousData ?? { id: params.id }, String(params.id)),
    };
}
